import { ICON_MAPPING, getIconComponent } from "@storytell/bsky-icons";
import { useCallOptions, useLabelClient, withBearerToken } from "@storytell/cp-client";
import { dispatchGlobalEvent } from "@storytell/events";
import type { AssetId, Label, LabelId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbPlus, TbTags, TbX } from "solid-icons/tb";
import { type Component, For, Show, createSignal } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { truncateMiddle } from "~/utils/truncateMiddle";
import { useWire } from "~/wire";
import { isLightColor } from "../layouts/minimal/isLightColor";
import { AssetLabelPicker } from "./AssetLabelPicker";
import { BulkAssetLabelPicker } from "./BulkAssetLabelPicker";
import { LabelTooltip } from "./LabelTooltip";

export const AssetLabelsRow: Component<{
  assetId: AssetId;
  mount: Node | undefined;
  setSearch: (s: string) => void;
  addFilter: (s: string) => void;
  selectedAssets?: string[];
}> = (props) => {
  const wire = useWire();

  const [labelPickerOpen, setLabelPickerOpen] = createSignal(false);
  const [expanded, setExpanded] = createSignal(false);
  const assetLabels = () =>
    wire.sync
      .getAssetRelatedLabels(props.assetId)
      .map((l) => wire.sync.labels.get(l))
      .filter(Boolean)
      .toSorted((a, b) => a?.name.localeCompare(b?.name || "") ?? 0) as Label[];

  const clamped = () => (assetLabels().length <= 3 ? assetLabels() : assetLabels().slice(0, 2));
  const rest = () => (assetLabels().length <= 3 ? [] : assetLabels().slice(2));

  const onRemoveLabelFromAsset = async (labelId: string) => {
    const client = useLabelClient();
    const existing = wire.sync.getAssetHasLabel(props.assetId, labelId as LabelId);
    if (!existing) return;

    wire.sync.removeLabelFromAsset(props.assetId, labelId as LabelId);
    try {
      const result = await client.disassociateLabel(
        {
          labelId,
          resourceId: props.assetId,
          resourceType: "asset",
        },
        useCallOptions(withBearerToken(wire.auth.token())),
      );
      if (result.isErr()) {
        wire.sync.addLabelToAsset(props.assetId, labelId as LabelId);
      }
    } catch (error) {
      console.error(error);
      wire.sync.addLabelToAsset(props.assetId, labelId as LabelId);
    }
  };

  return (
    <div
      class="flex items-center gap-1.5 relative"
      classList={{
        "flex-wrap": expanded(),
      }}
    >
      <StPopover
        noArrow
        gutter={10}
        placement="bottom-start"
        portalProps={{
          mount: props.mount,
        }}
        contentProps={{
          class: "z-50",
        }}
        open={labelPickerOpen()}
        onOpenChange={setLabelPickerOpen}
        content={
          <Show
            when={(props.selectedAssets?.length ?? 0) > 1}
            fallback={
              <AssetLabelPicker
                assetId={props.assetId}
                assetLabels={assetLabels()}
                mount={props.mount}
                onLabelCreated={async () => {
                  setLabelPickerOpen(false);
                  dispatchGlobalEvent("refresh-sync", undefined);
                }}
              />
            }
          >
            <BulkAssetLabelPicker
              onLabelCreated={() => {
                setLabelPickerOpen(false);
                dispatchGlobalEvent("refresh-sync", undefined);
              }}
              onClose={() => setLabelPickerOpen(false)}
              mount={props.mount}
              selected={props.selectedAssets as AssetId[]}
            />
          </Show>
        }
      >
        <StPopoverTrigger as={StButton} icon={TbPlus} iconClass="size-3" />
      </StPopover>
      <For each={expanded() ? assetLabels() : clamped()}>
        {(label) => (
          <StTooltip
            placement="top"
            portalProps={{
              mount: props.mount,
            }}
            contentProps={{
              class: "z-50",
            }}
            content={<LabelTooltip labelId={label.id} />}
          >
            <StTooltipTrigger
              as={StButton}
              onClick={() => {
                props.addFilter(label.id);
              }}
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              icon={(props: any) => (
                <>
                  <Dynamic
                    component={getIconComponent(label.icon)}
                    {...props}
                    class={twMerge(props.class, "group-hover/label:hidden")}
                  />
                  <TbX
                    {...props}
                    class={twMerge(props.class, "hidden group-hover/label:block")}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopImmediatePropagation();
                      onRemoveLabelFromAsset(label.id);
                    }}
                  />
                </>
              )}
              class="p-1 shrink-0 whitespace-nowrap group/label bg-tile hover:bg-tile-hover"
              iconClass="size-4 p-0.5 rounded-full bg-[var(--label-color)] text-[var(--label-icon)]"
              style={{
                "--label-color": label.color,
                "--label-icon": isLightColor(label.color) ? "black" : "white",
              }}
            >
              {truncateMiddle(label.name)}
            </StTooltipTrigger>
          </StTooltip>
        )}
      </For>
      <Show when={rest().length}>
        <StButton
          class="p-1 shrink-0 whitespace-nowrap"
          iconClass="size-3.5"
          icon={TbTags}
          onClick={() => setExpanded((s) => !s)}
        >
          {expanded() ? "Hide" : `${rest().length} More`}
        </StButton>
      </Show>
    </div>
  );
};
